import React, { useState, useEffect } from 'react';
import './ResellerPortal.css';
import ResellerAuth from './ResellerAuth';
import ResellerDashboard from './ResellerDashboard';
import { ResellCatalog, MyNetwork, EarningsWallet, MarketingKit, ResellerSupport } from './ResellerSubpages';
import { clearAuth } from './resellerapi';

// ── Nav config ──
const NAV_GROUPS = [
  {
    title: 'Overview',
    items: [
      { id: 'dashboard', label: 'Dashboard',     icon: '◈' },
    ],
  },
  {
    title: 'Business',
    items: [
      { id: 'catalog',   label: 'Product Catalog', icon: '⊞' },
      { id: 'network',   label: 'My Sales',         icon: '↗' },
      { id: 'wallet',    label: 'Earnings',          icon: '◎' },
    ],
  },
  {
    title: 'Tools',
    items: [
      { id: 'marketing', label: 'Marketing Kit',   icon: '✦' },
      { id: 'support',   label: 'Support',          icon: '◐' },
    ],
  },
];

const PAGE_TITLES = {
  dashboard: 'Dashboard',
  catalog:   'Product Catalog',
  network:   'My Sales & Network',
  wallet:    'Earnings & Wallet',
  marketing: 'Marketing Kit',
  support:   'Support',
};

// ── Tier helper ──
const getTierLabel = (sales = 0) => {
  if (sales >= 50) return { label: 'Gold',   class: 'gold' };
  if (sales >= 20) return { label: 'Silver', class: 'silver' };
  return               { label: 'Bronze',  class: 'bronze' };
};

const ResellerPortal = () => {
  const [user,    setUser]    = useState(() => {
    try { return JSON.parse(localStorage.getItem('reseller_user') || 'null'); }
    catch { return null; }
  });
  const [authed,  setAuthed]  = useState(!!localStorage.getItem('reseller_token'));
  const [tab,     setTab]     = useState('dashboard');
  const [balance, setBalance] = useState(null);
  const [stats,   setStats]   = useState(null);

  // Load minimal data for sidebar wallet
  useEffect(() => {
    if (!authed) return;
    import('./resellerapi').then(({ walletAPI, dashboardAPI }) => {
      walletAPI.getBalance().then(r => setBalance(r.data)).catch(() => {});
      dashboardAPI.getStats().then(r => setStats(r.data)).catch(() => {});
    });
  }, [authed]);

  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setAuthed(true);
  };

  const handleLogout = () => {
    import('./resellerapi').then(({ authAPI }) => authAPI.logout().catch(() => {}));
    clearAuth();
    setUser(null);
    setAuthed(false);
  };

  const tier = getTierLabel(stats?.sales_this_month || 0);
  const availableBalance = balance?.available || 0;

  const handleWithdraw = () => setTab('wallet');

  // ── NOT AUTHENTICATED ──
  if (!authed) {
    return <ResellerAuth onAuthSuccess={handleAuthSuccess} />;
  }

  // ── AUTHENTICATED ──
  return (
    <div className="res-portal-root">

      {/* ── SIDEBAR ── */}
      <aside className="res-sidebar">
        {/* Logo */}
        <div className="res-sidebar-logo">
          <div className="sidebar-hex">A</div>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-main">ATR RESELLER</span>
            <span className="sidebar-brand-sub">PARTNER HUB</span>
          </div>
        </div>

        {/* User card */}
        <div className="res-sidebar-user">
          <div className="sidebar-avatar">{user?.name?.[0]?.toUpperCase() || 'R'}</div>
          <div>
            <div className="sidebar-user-name">{user?.name || 'Partner'}</div>
            <div className="sidebar-user-id">ID: #{user?.ref_code || user?.id || 'RES-000'}</div>
          </div>
        </div>

        {/* Tier */}
        <div className="sidebar-tier">
          <span className={`tier-pill ${tier.class}`}>
            <span className="tier-dot" />
            {tier.label} Tier
          </span>
        </div>

        {/* Navigation */}
        <nav className="res-nav">
          {NAV_GROUPS.map((group, gi) => (
            <div key={gi} className="res-nav-group">
              <span className="res-nav-group-title">{group.title}</span>
              {group.items.map(item => (
                <button
                  key={item.id}
                  className={`res-nav-item ${tab === item.id ? 'active' : ''}`}
                  onClick={() => setTab(item.id)}
                >
                  <span className="nav-icon-wrap">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.id === 'support' && (
                    <span className="nav-badge">24h</span>
                  )}
                </button>
              ))}
            </div>
          ))}

          {/* Logout */}
          <div className="res-nav-group">
            <button
              className="res-nav-item"
              onClick={handleLogout}
              style={{ color: 'var(--red-500)' }}
            >
              <span className="nav-icon-wrap">✕</span>
              <span>Logout</span>
            </button>
          </div>
        </nav>

        {/* Wallet quick view */}
        <div className="sidebar-wallet">
          <div className="wallet-label">WALLET BALANCE</div>
          <div className="wallet-amount">
            ₹{Number(availableBalance).toLocaleString('en-IN')}
          </div>
          <button className="sidebar-withdraw-btn" onClick={handleWithdraw}>
            Withdraw Earnings →
          </button>
        </div>
      </aside>

      {/* ── MAIN AREA ── */}
      <main className="res-main">

        {/* Topbar */}
        <header className="res-topbar">
          <div className="topbar-page-title">{PAGE_TITLES[tab]}</div>
          <div className="topbar-right">
            <div className="topbar-stat">
              <span className="topbar-stat-label">Total Earned</span>
              <span className="topbar-stat-val">
                ₹{Number(stats?.total_earned || 0).toLocaleString('en-IN')}
              </span>
            </div>
            <div className="topbar-stat">
              <span className="topbar-stat-label">This Month</span>
              <span className="topbar-stat-val">
                {stats?.sales_this_month || 0} sales
              </span>
            </div>
            <button className="topbar-withdraw-btn" onClick={handleWithdraw}>
              Withdraw →
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="res-content-area">
          {tab === 'dashboard' && <ResellerDashboard user={user} />}
          {tab === 'catalog'   && <ResellCatalog user={user} />}
          {tab === 'network'   && <MyNetwork />}
          {tab === 'wallet'    && <EarningsWallet />}
          {tab === 'marketing' && <MarketingKit />}
          {tab === 'support'   && <ResellerSupport />}
        </div>
      </main>
    </div>
  );
};

export default ResellerPortal;